<template>
  <div v-if="order" class="vp-root">
    <div class="vp-topbar">
      <div class="vp-topbar__title">
        Vídeo:
        <span class="vp-topbar__order-name">{{ order.orderNumber }}</span>
      </div>
      <button
        class="vp-topbar__panel-btn"
        :class="{ 'vp-topbar__panel-btn--active': showPanel }"
        @click="showPanel = !showPanel"
      >
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
          <path
            d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"
            stroke="currentColor"
            stroke-width="2"
            fill="none"
          />
          <circle
            cx="12"
            cy="12"
            r="3"
            stroke="currentColor"
            stroke-width="2"
            fill="none"
          />
        </svg>
        Detalles
      </button>
      <button
        class="vp-topbar__close-btn"
        title="Cerrar"
        @click="$emit('close')"
      >
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
          <line
            x1="18"
            y1="6"
            x2="6"
            y2="18"
            stroke="currentColor"
            stroke-width="2.5"
            stroke-linecap="round"
          />
          <line
            x1="6"
            y1="6"
            x2="18"
            y2="18"
            stroke="currentColor"
            stroke-width="2.5"
            stroke-linecap="round"
          />
        </svg>
      </button>
    </div>
    <div class="vp-main">
      <div class="vp-video-col">
        <div class="vp-canvas">
          <transition name="fade"
            ><div v-if="activeEventId" class="vp-event-badge">
              {{ activeEventEventId }}
            </div></transition
          >
          <div class="vp-canvas__video-wrapper">
            <video
              ref="videoplayer"
              id="videoplayer"
              preload
              muted
              @click="actionPlayToggle"
              @timeupdate="updateTime"
            >
              <source
                :src="videoUrl"
                type="video/mp4"
                @error="handleVideoError"
              />
            </video>
          </div>
        </div>
        <div class="vp-controls">
          <VideoTimeline
            ref="videoTimeline"
            :visible-time-marks="visibleTimeMarks"
            :segments="segments"
            :event-segments="eventSegments"
            :context-event-segments="contextEventSegments"
            :context-events="contextEvents"
            :time-marks-data="timeMarksData"
            :section-collapsed="sectionCollapsed"
            :player-current-time="playerCurrentTime"
            :total-seconds="totalSeconds"
            :is-cut-mode-enabled="isCutModeEnabled"
            :cut-start="cutStart"
            :cut-end="cutEnd"
            :tooltip-visible="tooltipVisible"
            :tooltip-x="tooltipX"
            :tooltip-time="tooltipTime"
            :is-group-master="isGroupMaster"
            :active-event-id="activeEventId"
            @seek="seekVideo"
            @show-tooltip="showTooltip"
            @hide-tooltip="hideTooltip"
            @start-drag="startDrag"
            @start-handle-drag="startHandleDrag"
          />
          <VideoControls
            :is-playing="isPlaying"
            :is-cut-mode-enabled="isCutModeEnabled"
            :available-speeds="availableSpeeds"
            :speed="speed"
            @backward="actionBackward"
            @play-toggle="actionPlayToggle"
            @stop="actionStop"
            @forward="actionForward"
            @photo="actionPhoto"
            @video="actionVideo"
            @expand="actionExpand"
            @speed="actionSpeed"
          />
        </div>
      </div>

      <transition name="slide-panel">
        <VideoSidePanel
          v-if="showPanel"
          :order="order"
          :events="events"
          :processed-events-for-milestones="processedEventsForMilestones"
          :seconds="seconds"
          :section-collapsed="sectionCollapsed"
          :context-events="contextEvents"
          :loading-context-events="loadingContextEvents"
          :active-context-event-id="activeContextEventId"
          :ai-events-enabled="aiEventsEnabled"
          @toggle-order-data="
            sectionCollapsed.orderData = !sectionCollapsed.orderData
          "
          @toggle-events="sectionCollapsed.events = !sectionCollapsed.events"
          @toggle-context-events="toggleContextEventsSection"
          @goto="positionCursorTime"
          @select-context-event="
            (evt) => positionCursorTime(getContextEventOffset(evt))
          "
          @delete-context-event="deleteContextEvent"
        />
      </transition>
    </div>
  </div>
</template>

<script>
import moment from "moment";
import { mapState } from "vuex";
import apiToken from "@/components/utils/apiToken";
import ordersApi from "@/services/orders.api.js";
import storage from "@/components/utils/storage";
import videoApi from "@/services/video.api";
import contextEventsApi from "@/services/contextEvents.api.js";
import VideoControls from "./VideoControls.vue";
import VideoSidePanel from "./VideoSidePanel.vue";
import VideoTimeline from "./VideoTimeline.vue";
export default {
  name: "VideoPlayer",
  props: ["order", "startOrder", "segmentsData", "eventsData", "timeMarksData"],
  components: { VideoControls, VideoSidePanel, VideoTimeline },
  data() {
    return {
      API_URL: process.env.VUE_APP_API_URL,
      speed: "x1",
      availableSpeeds: [0.25, 0.5, 1, 2, 4, 8, 16],
      player: {},
      valuesSlider: [0, 10],
      isPlaying: false,
      videoUrl: "",
      playerCurrentTime: 0,
      svg: null,
      updateInterval: null,
      time: [],
      frameStep: 1 / 30,
      isCutModeEnabled: false,
      maxValueSlider: 0,
      colorLabelSlider: { backgroundColor: "#00636A", borderColor: "#00636A" },
      colorBarSlider: { backgroundColor: "#00636A" },
      tooltipVisible: false,
      tooltipX: 0,
      tooltipTime: "",
      isDragging: false,
      cutStart: 0,
      cutEnd: 0,
      activeHandle: null,
      events: [],
      showPanel: true,
      sectionCollapsed: {
        orderData: true,
        events: true,
        contextEvents: true,
      },
      contextEvents: [],
      loadingContextEvents: false,
    };
  },
  computed: {
    ...mapState("config", ["aiEventsEnabled"]),
    date: function () {
      return moment(this.order.start_date)
        .add(this.playerCurrentTime, "seconds")
        .format("DD/MM/YYYY - HH:mm:ss");
    },
    activeEventId() {
      if (!this.order || !this.events.length) return null;
      const videoStartMs = new Date(this.order.videoStart).getTime();
      const currentMs = videoStartMs + this.playerCurrentTime * 1000;
      const activeEvent = this.events.find((evt) => {
        const startMs = new Date(evt.startedAt).getTime();
        const endMs = new Date(evt.endedAt).getTime();
        return currentMs >= startMs && currentMs <= endMs;
      });
      return activeEvent ? activeEvent.id : null;
    },
    activeEventEventId() {
      if (!this.order || !this.events.length) return null;
      const videoStartMs = new Date(this.order.videoStart).getTime();
      const currentMs = videoStartMs + this.playerCurrentTime * 1000;
      const activeEvent = this.events.find((evt) => {
        const startMs = new Date(evt.startedAt).getTime();
        const endMs = new Date(evt.endedAt).getTime();
        return currentMs >= startMs && currentMs <= endMs;
      });
      return activeEvent ? activeEvent.eventId : null;
    },
    activeContextEventId() {
      if (!this.order || !this.contextEvents.length) return null;
      const videoStartMs = new Date(this.order.videoStart).getTime();
      const currentMs = videoStartMs + this.playerCurrentTime * 1000;
      const activeEvt = this.contextEvents.find((evt) => {
        const startMs = new Date(evt.initialTimestamp).getTime();
        const endMs = new Date(evt.finalTimestamp).getTime();
        return currentMs >= startMs && currentMs <= endMs;
      });
      return activeEvt ? activeEvt.id : null;
    },
    isGroupMaster() {
      return (
        this.order &&
        this.order.status === 5 &&
        this.order.groupId === this.order.id
      );
    },
    seconds() {
      return parseFloat(this.playerCurrentTime).toFixed(2);
    },
    references() {
      return this.$refs.videoplayer;
    },
    positionCursorValue() {
      return (100 * this.playerCurrentTime) / this.order.video_length + "%";
    },
    segments() {
      if (this.segmentsData && this.segmentsData.length > 0)
        return this.segmentsData;
      const segCount = 10;
      const segDur = this.totalSeconds / segCount;
      const segments = [];
      const base = new Date(this.order.videoStart).getTime();
      for (let i = 0; i < segCount; i++)
        segments.push({
          duration: segDur,
          videoStart: new Date(base + segDur * 1000 * i),
        });
      return segments;
    },
    processedEventsForMilestones() {
      if (this.eventsData && this.eventsData.length > 0) return this.eventsData;
      return this.events;
    },
    eventSegments() {
      if (!this.segmentsData || this.segmentsData.length === 0) {
        if (!this.order || !this.events.length) return [];
        const videoStartMs = new Date(this.order.videoStart).getTime();
        const videoEndMs = new Date(this.order.videoEnd).getTime();
        const totalMs = videoEndMs - videoStartMs;
        return this.events.map((evt) => {
          const startMs = new Date(evt.startedAt).getTime();
          const endMs = new Date(evt.endedAt).getTime();
          const leftPct = ((startMs - videoStartMs) / totalMs) * 100;
          const widthPct = ((endMs - startMs) / totalMs) * 100;
          return {
            id: evt.id,
            elementId: evt.eventId,
            leftPct: leftPct.toFixed(2),
            widthPct: widthPct.toFixed(2),
            color: evt.eventColor || "#86da00",
          };
        });
      }
      return this.processedEventsForMilestones
        .filter(
          (evt) => evt.videoStartOffset !== null && evt.videoEndOffset !== null
        )
        .map((evt) => {
          const totalDuration = this.totalSeconds;
          const leftPct = (evt.videoStartOffset / totalDuration) * 100;
          const widthPct =
            ((evt.videoEndOffset - evt.videoStartOffset) / totalDuration) * 100;
          return {
            id: evt.id,
            elementId: evt.eventId,
            leftPct: leftPct.toFixed(2),
            widthPct: widthPct.toFixed(2),
            color: evt.eventColor || "#86da00",
          };
        });
    },
    contextEventSegments() {
      if (!this.order || !this.contextEvents.length) return [];
      const videoStartMs = new Date(this.order.videoStart).getTime();
      return this.contextEvents
        .filter((evt) => evt.initialTimestamp && evt.finalTimestamp)
        .map((evt) => {
          const startMs = new Date(evt.initialTimestamp).getTime();
          const endMs = new Date(evt.finalTimestamp).getTime();
          const startOffset = (startMs - videoStartMs) / 1000;
          const endOffset = (endMs - videoStartMs) / 1000;
          const leftPct = (startOffset / this.totalSeconds) * 100;
          const widthPct = Math.max(
            ((endOffset - startOffset) / this.totalSeconds) * 100,
            0.3
          );
          return {
            id: evt.id,
            startOffset,
            leftPct: leftPct.toFixed(2),
            widthPct: widthPct.toFixed(2),
            type: evt.detectedEventType,
          };
        });
    },
    totalSeconds() {
      if (this.segmentsData && this.segmentsData.length > 0)
        return this.segmentsData.reduce(
          (total, seg) => total + seg.duration,
          0
        );
      const start = new Date(this.order.videoStart);
      const end = new Date(this.order.videoEnd);
      return (end - start) / 1000;
    },
    timeLabels() {
      return this.segments.map((seg) => this.formatSegmentTime(seg.videoStart));
    },
    timeMarks() {
      if (this.timeMarksData && this.timeMarksData.length > 0)
        return this.timeMarksData.map((tm) => new Date(tm.timestamp));
      if (this.segmentsData && this.segmentsData.length > 0) {
        const marks = [];
        const numMarks = 11;
        const totalDuration = this.totalSeconds;
        for (let i = 0; i < numMarks; i++) {
          const videoSeconds = (totalDuration / (numMarks - 1)) * i;
          let accumulatedTime = 0;
          let realTimestamp = null;
          for (const seg of this.segmentsData) {
            if (
              videoSeconds >= accumulatedTime &&
              videoSeconds <= accumulatedTime + seg.duration
            ) {
              const offsetInSegment = videoSeconds - accumulatedTime;
              const segmentStart = new Date(seg.startTime);
              realTimestamp = new Date(
                segmentStart.getTime() + offsetInSegment * 1000
              );
              break;
            }
            accumulatedTime += seg.duration;
          }
          if (realTimestamp) marks.push(realTimestamp);
        }
        return marks;
      }
      const marks = this.segments.map((seg) => seg.videoStart);
      marks.push(
        new Date(
          new Date(this.order.videoStart).getTime() + this.totalSeconds * 1000
        )
      );
      return marks;
    },
    visibleTimeMarks() {
      if (!this.timeMarksData || this.timeMarksData.length === 0) {
        const numMarks = 11;
        const marks = [];
        for (let i = 0; i < numMarks; i++) {
          const position = (i / (numMarks - 1)) * 100;
          const offsetSeconds = (this.totalSeconds / (numMarks - 1)) * i;
          const timestamp = new Date(
            new Date(this.order.videoStart).getTime() + offsetSeconds * 1000
          );
          let transform = "translateX(-50%)";
          if (i === 0) transform = "translateX(0)";
          else if (i === numMarks - 1) transform = "translateX(-100%)";
          marks.push({
            timestamp: timestamp.toISOString(),
            position,
            transform,
            offsetSeconds,
            isSeparator: false,
          });
        }
        return marks;
      }
      const visible = [];
      const minSegmentDurationForIntermediate = 600;
      for (let i = 0; i < this.segmentsData.length; i++) {
        const seg = this.segmentsData[i];
        const prevSeg = i > 0 ? this.segmentsData[i - 1] : null;
        if (seg.type === "video") {
          const hasSeparatorBefore = prevSeg && prevSeg.type === "separator";
          if (!hasSeparatorBefore) {
            const startPosition = (seg.videoOffset / this.totalSeconds) * 100;
            visible.push({
              timestamp: seg.startTime,
              position: startPosition,
              transform: i === 0 ? "translateX(0)" : "translateX(-50%)",
              offsetSeconds: seg.videoOffset,
              isSeparator: false,
            });
          }
          if (seg.duration >= minSegmentDurationForIntermediate) {
            const middleOffset = seg.videoOffset + seg.duration / 2;
            const middlePosition = (middleOffset / this.totalSeconds) * 100;
            const middleTime = new Date(
              new Date(seg.startTime).getTime() + (seg.duration / 2) * 1000
            );
            visible.push({
              timestamp: middleTime.toISOString(),
              position: middlePosition,
              transform: "translateX(-50%)",
              offsetSeconds: middleOffset,
              isSeparator: false,
            });
          }
          if (i === this.segmentsData.length - 1) {
            const endPosition =
              ((seg.videoOffset + seg.duration) / this.totalSeconds) * 100;
            visible.push({
              timestamp: seg.endTime,
              position: endPosition,
              transform: "translateX(-100%)",
              offsetSeconds: seg.videoOffset + seg.duration,
              isSeparator: false,
            });
          }
        } else if (seg.type === "separator") {
          const nextSeg = this.segmentsData[i + 1];
          if (
            prevSeg &&
            prevSeg.type === "video" &&
            nextSeg &&
            nextSeg.type === "video"
          ) {
            const centerPosition =
              ((seg.videoOffset + seg.duration / 2) / this.totalSeconds) * 100;
            visible.push({
              endTimestamp: prevSeg.endTime,
              startTimestamp: nextSeg.startTime,
              position: centerPosition,
              transform: "translateX(-50%)",
              offsetSeconds: seg.videoOffset + seg.duration / 2,
              isSeparator: true,
            });
          }
        }
      }
      return visible;
    },
  },
  watch: {
    order: {
      handler: function () {
        this.load();
      },
      deep: true,
    },
    startVideo: {
      handler: function () {
        this.load();
      },
      deep: true,
    },
  },
  mounted() {
    this.player = this.$refs.videoplayer;
    if (!this.player) this.player = document.getElementById("videoplayer");
    this.svg = document.getElementById("svg");
    this.load();
  },
  methods: {
    async fetchEvents() {
      if (!this.order?.id) {
        this.events = [];
        return;
      }
      if (this.eventsData && this.eventsData.length > 0) {
        this.events = this.eventsData;
        return;
      }
      try {
        let response = await ordersApi.fetchEventsOfOrder({
          orderId: this.order.id,
        });
        this.events = Array.isArray(response.data) ? response.data : [];
      } catch (error) {
        console.error("Error al cargar eventos:", error);
        this.events = [];
        if (error.response?.status === 401) apiToken.tokenExpire(this);
      }
    },
    async fetchContextEvents() {
      if (!this.order?.recorderId) {
        this.contextEvents = [];
        return;
      }
      this.loadingContextEvents = true;
      try {
        const data = await contextEventsApi.fetchByRecorderAndDateRange(
          this.order.recorderId,
          this.order.videoStart,
          this.order.videoEnd
        );
        this.contextEvents = Array.isArray(data) ? data : [];
      } catch (error) {
        console.error("Error al cargar eventos IA:", error);
        this.contextEvents = [];
        if (error.response?.status === 401) apiToken.tokenExpire(this);
      } finally {
        this.loadingContextEvents = false;
      }
    },
    toggleContextEventsSection() {
      this.sectionCollapsed.contextEvents =
        !this.sectionCollapsed.contextEvents;
      if (
        this.aiEventsEnabled &&
        !this.sectionCollapsed.contextEvents &&
        this.contextEvents.length === 0
      ) {
        this.fetchContextEvents();
      }
    },
    async deleteContextEvent(id) {
      try {
        await this.$confirm(
          "¿Seguro que quieres eliminar este evento detectado por IA?",
          "Eliminar evento",
          {
            confirmButtonText: "Eliminar",
            cancelButtonText: "Cancelar",
            type: "warning",
          }
        );
      } catch {
        return;
      }
      try {
        await contextEventsApi.deleteById(id);
        this.contextEvents = this.contextEvents.filter((evt) => evt.id !== id);
      } catch (error) {
        console.error("Error al eliminar evento IA:", error);
        if (error.response?.status === 401) apiToken.tokenExpire(this);
        else this.$message.error("No se ha podido eliminar el evento.");
      }
    },
    getContextEventOffset(evt) {
      const videoStartMs = new Date(this.order.videoStart).getTime();
      const startMs = new Date(evt.initialTimestamp).getTime();
      return (startMs - videoStartMs) / 1000;
    },
    getTimeMarkWidth(index) {
      if (!this.timeMarksData || this.timeMarksData.length === 0)
        return 100 / 11;
      if (index === 0) {
        const nextOffset =
          this.timeMarksData[1]?.videoOffset || this.totalSeconds;
        return (nextOffset / this.totalSeconds) * 100;
      } else if (index === this.timeMarksData.length - 1) {
        const prevOffset = this.timeMarksData[index - 1].videoOffset;
        const currentOffset = this.timeMarksData[index].videoOffset;
        return ((currentOffset - prevOffset) / this.totalSeconds) * 100;
      } else {
        const prevOffset = this.timeMarksData[index - 1].videoOffset;
        const nextOffset = this.timeMarksData[index + 1].videoOffset;
        return ((nextOffset - prevOffset) / (2 * this.totalSeconds)) * 100 * 2;
      }
    },
    isTimeMarkSeparator(index) {
      if (this.timeMarksData && this.timeMarksData.length > 0)
        return this.timeMarksData[index].isSeparator === true;
      return false;
    },
    formatTimeMarkLabel(dateTime, index) {
      if (this.isTimeMarkSeparator(index)) return "⋯";
      const d = new Date(dateTime);
      return d.toLocaleTimeString([], {
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
      });
    },
    handleVideoError(event) {
      console.error("Error al cargar el video:", event);
      this.$message.error(
        "El vídeo no se ha generado todavía o no está disponible."
      );
    },
    startHandleDrag(handle) {
      this.activeHandle = handle;
      window.addEventListener("mousemove", this.onHandleDrag);
      window.addEventListener("mouseup", this.stopHandleDrag);
    },
    onHandleDrag(event) {
      if (!this.activeHandle) return;
      const bar = this.$refs.videoTimeline.$refs.timelineBar;
      if (!bar) return;
      const rect = bar.getBoundingClientRect();
      let x = event.clientX - rect.left;
      x = Math.max(0, Math.min(x, rect.width));
      const time = (x / rect.width) * this.totalSeconds;
      if (this.activeHandle === "start")
        this.cutStart = Math.min(time, this.cutEnd || this.totalSeconds);
      else this.cutEnd = Math.max(time, this.cutStart);
    },
    stopHandleDrag() {
      this.activeHandle = null;
      window.removeEventListener("mousemove", this.onHandleDrag);
      window.removeEventListener("mouseup", this.stopHandleDrag);
    },
    startDrag(event) {
      this.isDragging = true;
      window.addEventListener("mousemove", this.onDrag);
      window.addEventListener("mouseup", this.stopDrag);
      this.onDrag(event);
    },
    onDrag(event) {
      if (!this.isDragging) return;
      const bar = this.$refs.videoTimeline.$refs.timelineBar;
      if (!bar) return;
      const rect = bar.getBoundingClientRect();
      let x = event.clientX - rect.left;
      x = Math.max(0, Math.min(x, rect.width));
      const ratio = x / rect.width;
      const newTime = ratio * this.totalSeconds;
      this.player.currentTime = newTime;
      this.playerCurrentTime = newTime;
    },
    stopDrag() {
      this.isDragging = false;
      window.removeEventListener("mousemove", this.onDrag);
      window.removeEventListener("mouseup", this.stopDrag);
    },
    showTooltip(event) {
      const bar = this.$refs.videoTimeline.$refs.timelineBar;
      if (!bar) return;
      const rect = bar.getBoundingClientRect();
      const x = event.clientX - rect.left;
      const ratio = x / rect.width;
      const seconds = ratio * this.totalSeconds;
      const date = new Date(
        new Date(this.order.videoStart).getTime() + seconds * 1000
      );
      const hh = String(date.getHours()).padStart(2, "0");
      const mm = String(date.getMinutes()).padStart(2, "0");
      const ss = String(date.getSeconds()).padStart(2, "0");
      this.tooltipTime = `${hh}:${mm}:${ss}`;
      this.tooltipX = x;
      this.tooltipVisible = true;
    },
    hideTooltip() {
      this.tooltipVisible = false;
    },
    seekVideo(event) {
      if (this.isCutModeEnabled) return;
      const bar = this.$refs.videoTimeline.$refs.timelineBar;
      const rect = bar.getBoundingClientRect();
      const clickX = event.clientX - rect.left;
      const ratio = clickX / rect.width;
      const newTime = ratio * this.totalSeconds;
      this.player.currentTime = newTime;
      this.playerCurrentTime = newTime;
    },
    updateTime(e) {
      this.playerCurrentTime = e.target.currentTime;
    },
    load: function () {
      if (this.order) {
        const isSwRegistered = storage.loadSwStatus();
        if (isSwRegistered === "true")
          this.videoUrl = `${this.API_URL}/videos/` + this.order.id;
        else {
          const token = storage.loadToken();
          this.videoUrl = `${this.API_URL}/videos/token/${this.order.id}?token=${token}`;
        }
        this.maxValueSlider = this.order.video_length;
        let aux = this.order.video_length / 10;
        for (let i = 0; i < 10; i++)
          this.time.push(
            moment(this.order.videoStart)
              .add(aux * i, "seconds")
              .format("HH:mm")
          );
        this.positionCursor((this.startVideo * 100) / this.order.video_length);
        this.fetchEvents();
        this.contextEvents = [];
        if (this.aiEventsEnabled && !this.sectionCollapsed.contextEvents) {
          this.fetchContextEvents();
        }
      }
    },
    actionBackward() {
      if (!this.player) return;
      const newTime = Math.max(this.player.currentTime - this.frameStep, 0);
      this.player.currentTime = newTime;
      this.playerCurrentTime = newTime;
    },
    actionForward() {
      if (!this.player) return;
      const max = this.player.duration || 0;
      const newTime = Math.min(this.player.currentTime + this.frameStep, max);
      this.player.currentTime = newTime;
      this.playerCurrentTime = newTime;
    },
    actionPlay() {
      if (!this.player) return;
      this.isPlaying = true;
      this.player.play().catch((err) => {
        if (err.name !== "AbortError")
          console.error("Error al reproducir:", err);
      });
    },
    actionPause: function () {
      if (this.player) {
        this.isPlaying = false;
        this.player.pause();
      }
    },
    actionPlayToggle() {
      if (!this.player) return;
      if (this.isPlaying) {
        this.player.pause();
        this.isPlaying = false;
      } else {
        this.isPlaying = true;
        this.player.play().catch((err) => {
          if (err.name !== "AbortError")
            console.error("Error al reproducir:", err);
        });
      }
    },
    clickBarPr: function (e) {
      var rect = e.target.getBoundingClientRect();
      var x = e.clientX - rect.left;
      this.positionCursorNoPause((x * 100) / rect.width);
    },
    actionStop: function () {
      if (this.player) {
        this.isPlaying = false;
        this.positionCursor(0);
      }
    },
    actionSpeed: function (speed) {
      this.player.playbackRate = speed;
      this.speed = `x${speed}`;
    },
    positionCursorNoPause(newCursor) {
      this.actionPause();
      this.player.currentTime = (newCursor * this.order.video_length) / 100;
      this.actionPlay();
    },
    positionCursorTime(time) {
      this.actionPause();
      this.player.currentTime = time;
      this.actionPlay();
    },
    positionCursor(newCursor) {
      this.actionPause();
      this.player.currentTime =
        (newCursor * this.order.video_length) / 100 || 0;
      console.log(this.player.currentTime);
    },
    actionExpand: function () {
      if (this.player.requestFullscreen) this.player.requestFullscreen();
      else if (this.player.mozRequestFullScreen)
        this.player.mozRequestFullScreen();
      else if (this.player.webkitRequestFullscreen)
        this.player.webkitRequestFullscreen();
      else if (this.player.msRequestFullscreen)
        this.player.msRequestFullscreen();
    },
    actionPhoto() {
      videoApi
        .downloadScreenshot(this.order.id, this.playerCurrentTime.toFixed(2))
        .then((response) => {
          const blob = response.data;
          const blobUrl = window.URL.createObjectURL(blob);
          const link = document.createElement("a");
          link.href = blobUrl;
          link.download = "screenshot.jpg";
          document.body.appendChild(link);
          link.click();
          link.remove();
        })
        .catch((error) => {
          if (error.response?.status === 401) apiToken.tokenExpire(this);
          else console.error("Error descargando screenshot:", error);
        });
    },
    async actionVideo() {
      this.actionPause();
      if (!this.isCutModeEnabled) {
        this.cutStart = this.playerCurrentTime;
        this.cutEnd = this.totalSeconds;
        this.isCutModeEnabled = true;
      } else {
        this.isCutModeEnabled = false;
        try {
          await this.$confirm(this.$t("main.videos.exportVideoConfirmation"), {
            customClass: "messageModal",
          });
          const start = this.cutStart.toFixed(2);
          const end = this.cutEnd.toFixed(2);
          const response = await videoApi.downloadVideoSegment(
            this.order.id,
            start,
            end
          );
          const blobUrl = URL.createObjectURL(response.data);
          const link = document.createElement("a");
          link.href = blobUrl;
          link.download = `${this.order.id}-${start}-${end}.mp4`;
          document.body.appendChild(link);
          link.click();
          link.remove();
        } catch {
          this.isCutModeEnabled = false;
        }
      }
    },
    formatter: function (value) {
      return moment(this.order.start_date)
        .add(value, "seconds")
        .format("HH:mm:ss");
    },
  },
};
</script>

<style>
:root {
  --vp-accent: #0284c7;
  --vp-accent-light: rgba(2, 132, 199, 0.12);
  --vp-accent-ring: rgba(2, 132, 199, 0.35);
  --vp-success: #16a34a;
  --vp-success-bg: rgba(22, 163, 74, 0.12);
  --vp-ai: #7c3aed;
  --vp-ai-light: rgba(124, 58, 237, 0.12);
  --vp-bg: #f0f4f8;
  --vp-card: #ffffff;
  --vp-border: #e2e8f0;
  --vp-text: #0f172a;
  --vp-muted: #64748b;
  --vp-radius: 10px;
  --vp-shadow: 0 1px 3px rgba(0, 0, 0, 0.07);
  --vp-shadow-md: 0 4px 8px rgba(0, 0, 0, 0.06);
}
.vp-root {
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
  max-width: 100%;
  max-height: 100%;
  background: var(--vp-bg);
  font-family: "DM Sans", sans-serif;
  overflow: hidden;
  box-sizing: border-box;
}
.vp-topbar {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 18px;
  background: var(--vp-card);
  border-bottom: 1px solid var(--vp-border);
  flex-shrink: 0;
  box-shadow: var(--vp-shadow);
}
.vp-topbar__title {
  flex: 1;
  font-size: 15px;
  font-weight: 700;
  color: var(--vp-text);
}
.vp-topbar__order-name {
  color: var(--vp-accent);
}
.vp-topbar__panel-btn {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  border-radius: var(--vp-radius);
  background: var(--vp-card);
  border: 1.5px solid var(--vp-border);
  color: var(--vp-muted);
  cursor: pointer;
  font-size: 12px;
  font-family: inherit;
  transition: all 0.15s;
}
.vp-topbar__panel-btn:hover,
.vp-topbar__panel-btn--active {
  border-color: var(--vp-accent);
  color: var(--vp-accent);
  background: var(--vp-accent-light);
}
.vp-topbar__close-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 30px;
  border-radius: 50%;
  background-color: #ef4444;
  border: none;
  color: #fff;
  cursor: pointer;
  flex-shrink: 0;
  box-shadow: 0 2px 6px rgba(239, 68, 68, 0.4);
  transition: background-color 0.15s, transform 0.15s;
}
.vp-topbar__close-btn:hover {
  background-color: #dc2626;
  transform: scale(1.08);
}
.vp-main {
  flex: 1;
  display: flex;
  overflow: hidden;
}
.vp-video-col {
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  min-width: 0;
}
.vp-canvas {
  flex: 1;
  position: relative;
  background: linear-gradient(160deg, #111827, #1e2d3d 55%, #111827);
  overflow: hidden;
}
.vp-canvas__video-wrapper {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
}
.vp-canvas__video-wrapper video {
  width: 100%;
  height: 100%;
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
}
.vp-event-badge {
  position: absolute;
  top: 12px;
  right: 14px;
  background: #22c55e;
  color: #fff;
  font-size: 12px;
  font-weight: 700;
  padding: 3px 12px;
  border-radius: 20px;
  z-index: 10;
  box-shadow: 0 2px 10px rgba(34, 197, 94, 0.45);
  letter-spacing: 0.02em;
  pointer-events: none;
}
.vp-controls {
  background: var(--vp-card);
  border-top: 1px solid var(--vp-border);
  padding: 10px 20px 14px;
  flex-shrink: 0;
}

/* Transiciones */
.slide-panel-enter-active,
.slide-panel-leave-active {
  transition: all 0.2s ease;
  overflow: hidden;
}
.slide-panel-enter,
.slide-panel-leave-to {
  width: 0;
  opacity: 0;
}
.slide-panel-enter-to,
.slide-panel-leave {
  width: 256px;
  opacity: 1;
}
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s ease;
}
.fade-enter,
.fade-leave-to {
  opacity: 0;
}

/* Diálogo */
.el-dialog.is-fullscreen {
  display: flex;
  flex-direction: column;
  overflow: hidden !important;
  max-width: 100vw;
  box-sizing: border-box;
}
.el-dialog__header {
  padding: 0 !important;
  flex-shrink: 0;
}
.el-dialog__body {
  padding: 0 !important;
  flex: 1;
  overflow: hidden !important;
  display: flex;
  flex-direction: column;
  max-width: 100%;
  box-sizing: border-box;
}
.el-dialog__headerbtn .el-dialog__close {
  contain: inline-size;
  font-size: x-large;
  margin-right: 4px;
}

@media (min-width: 2560px) {
  .vp-side-panel {
    width: 340px;
  }
}
</style>
